import { formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { ClientSecurityTransaction } from "@models/security-transactions/client-security-transaction";
import { SecurityTransactionType } from "@models/security-transactions/security-transaction-type.enum";
import {
  ColumnForExportComponent
} from "@pages/shares/components/shares-page/components/column-for-export/column-for-export.component";
import { ShareGroupView } from "@pages/shares/models/share-group-view";
import { ShareTypePipe } from "@shared/pipes/share-type.pipe";
import {
  ColDef,
  ColGroupDef,
  CsvExportParams,
  ExcelCell,
  ExcelExportParams,
  ExcelRow,
  ExcelStyle,
  GridApi,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
  ProcessRowGroupForExportParams
} from "ag-grid-community";
import { deepClone } from "fast-json-patch/commonjs/core";
import { take } from "rxjs";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

@Injectable()
export class SharesExportService {
  private modalFormsService = inject(ModalFormsService);

  groupExportToExcel(
    gridApi: GridApi,
    colDefs: ColDef[],
  ): void {
    const columnsForExport: string[] = [];
    (colDefs as ((ColDef & ColGroupDef)[]))
      .forEach((colDef) => {
        if (!colDef.hide && !colDef.children?.length && colDef.headerName && colDef.field) {
          columnsForExport.push(colDef.field);
        } else if (colDef?.children?.length) {
          colDef.children.forEach(child => {
            if (!(child as ColDef).hide) {
              const field = (child as ColDef).field ?? '';
              columnsForExport.push(field);
            }
          });
        }
      });

    // const excelExportParams = this.getGroupExcelExportParams(gridApi, colDefs);
    const numberColumnForExport = columnsForExport.length - 1;
    const exportParamsXls = this.exportParamsXls(numberColumnForExport);
    gridApi.exportDataAsExcel({ ...exportParamsXls, columnKeys: columnsForExport });
  }

  exportTransactionsList(
    gridApi: GridApi,
    colDefs: ColDef[],
    checkedShares: ClientSecurityTransaction[],
    clientSecurityTransaction: ClientSecurityTransaction[],
    isBulkExport: boolean,
    exportType: ExportTypeEnum
  ): void {
    const modalRef = this.modalFormsService.openModal(ColumnForExportComponent, { modalDialogClass: 'export-company-list' });
    const componentInstance = modalRef.componentInstance as ColumnForExportComponent;

    componentInstance.title = 'Export Share List';
    componentInstance.subTitle = 'shares selected';
    componentInstance.colDefs = deepClone(colDefs) as ColGroupDef[];
    componentInstance.numberOfCompanies = isBulkExport ? checkedShares.length : clientSecurityTransaction.length;
    componentInstance.exportType = exportType;
    componentInstance.confirm
      .pipe(take(1))
      .subscribe((columnForExport: string[]) => {
        const numberColumnForExport = columnForExport.length - 1;

        if (exportType === ExportTypeEnum.EXCEL) {
          const params: ExcelExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          const exportParamsXls = this.exportParamsXls(numberColumnForExport);
          gridApi.exportDataAsExcel({ ...exportParamsXls, ...params });
        } else if (exportType === ExportTypeEnum.CSV) {
          const params: CsvExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          gridApi.exportDataAsCsv(params);
        }
      });
  }

  readonly excelStyles: ExcelStyle[] = [
    {
      id: 'titleStyle',
      font: {
        size: 18,
        color: '#FFC82C',
        bold: true,
      },
      alignment: {
        horizontal: 'Center',
      },
    },
    {
      id: 'dateStyle',
      font: {
        size: 14,
        color: '#FFC82C',
      },
      alignment: {
        horizontal: 'Center',
      },
    },
    {
      id: "header",
      interior: {
        color: "#aaaaaa",
        pattern: "Solid",
      },
      font: {
        size: 11,
        bold: true,
      },
    },
  ];

  getExcelTitleRows: (numberColumnForExport: number) => ExcelRow[] = (numberColumnForExport) => [
    {
      cells: [
        {
          data: {
            value: 'Shares Report',
            type: "String",
          },
          mergeAcross: numberColumnForExport,
          styleId: 'titleStyle',
        },
      ],
    },
    {
      cells: [
        {
          data: {
            value: formatDate(new Date, 'dd/MM/yyyy', 'en-US'),
            type: "String",
          },
          mergeAcross: numberColumnForExport,
          styleId: 'dateStyle',
        },
      ],
    },
  ];

  exportParamsXls(numberColumnForExport: number = 0): ExcelExportParams {
    return {
      ...this.exportParams(),
      prependContent: this.getExcelTitleRows(numberColumnForExport)
    } as ExcelExportParams;
  }

  exportParamsCsv(): CsvExportParams {
    return this.exportParams() as CsvExportParams;
  }

  exportParams() {
    return {
      processCellCallback(params: ProcessCellForExportParams): string {
        const value: unknown = params.value;
        const shareData = params.node?.data as ClientSecurityTransaction;
        if (params.column.getColId() === 'transactionDate') {
          return formatDate(value as string ?? '', 'dd MMM yyyy', 'en-US');
        } else if (params.column.getColId() === 'transactionType') {
          return SecurityTransactionType[value as number];
        } else if (params.column.getColId() === 'holders') {
          return shareData?.holders ? shareData.holders.map((holder) => holder.name).join(', ') : '';
        } else if (params.column.getColId() === 'securityType.identifier') {
          return ShareTypePipe.formatValue(value as string, false);
        }

        return value as string;
      },
      processHeaderCallback(params: ProcessHeaderForExportParams) {
        const colDef = params.column.getColDef() as ColumnWithExportName;
        return colDef?.exportColumnName ?? colDef.headerName;
      },
      sheetName: 'Share',
      fileName: 'Share ' + formatDate(new Date(), 'dd-MM-yyyy', 'en-US')
    };
  }

  getGroupExcelExportParams(
    gridApi: GridApi,
    colDefs: ColDef[]
  ): ExcelExportParams {
    return {
      getCustomContentBelowRow: (params) => this.getGroupRows(params, gridApi, colDefs) as ExcelRow[],
      columnWidth: 120,
      sheetName: 'Grouped shares',
      fileName: 'Grouped shares ' + formatDate(new Date(), 'dd-MM-yyyy', 'en-US')
    };
  };


  getGroupRows = (
    params: ProcessRowGroupForExportParams,
    gridApi: GridApi,
    colDefs: ColDef[]
  ) => {
    const selectedColumns = gridApi.getAllDisplayedColumns()
      .map((col: { getColDef: () => ColumnWithExportName }) => col?.getColDef())
      .filter(col => col?.field);

    const headerNameCells = selectedColumns.map((col) => this.cell((col?.headerName ?? ''), 'header'));
    const headerValueCells = selectedColumns.map((col) => {
      const field = col?.field ?? '';
      const value = ((params.node.data as Record<string, unknown>)[field] ?? '') as string;
      if (field === 'shareholders') {
        const shareGroupView = params.node.data as ShareGroupView;

        const names = shareGroupView.shareholders
          .map((holder) => holder.name.trim())
          .join(', ');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return this.cell((names ?? '') as string, 'headerValue');
      }

      return this.cell(value, 'headerValue');
    });

    const colDefsDetail = (colDefs as (ColDef & ColGroupDef)[])
      .flatMap(col => {
        if (col?.children?.length) {
          return col.children.map((child: ColDef) => child);
        }
        return col;
      })
      .filter(col => !col.hide);

    const detailRows = (params.node.data as { callRecords: ClientSecurityTransaction[] }).callRecords.map((record) => {
      return [
        {
          outlineLevel: 1,
          cells: [
            this.cell(''),
            ...colDefsDetail.map((colDef) => {
              const field = colDef?.field ?? '';

              if (field === 'holders') {
                const names = record?.[field].map((holder) => holder.name.trim()).join(', ');
                return this.cell(names, 'body');
              } else if (field === 'transactionType') {
                return this.cell(SecurityTransactionType[record.transactionType as number], 'body');
              } else if (field === 'securityType.identifier') {
                return this.cell(record.securityType.identifier, 'body');
              } else if (field === 'transactionDate') {
                const transactionDate = formatDate(record[field as string] as string ?? '', 'dd MMM yyyy', 'en-US');
                return this.cell(transactionDate, 'body');
              }

              return this.cell(record[field] as string, 'body');
            })
          ],
        },
      ];
    });

    const rows = [
      {
        outlineLevel: 1,
        cells: headerNameCells
      },
      {
        outlineLevel: 1,
        cells: headerValueCells
      },
      {
        outlineLevel: 1,
        cells: [this.cell(''), ...colDefsDetail.map((col: ColumnWithExportName) => this.cell(col?.exportColumnName ?? col.headerName ?? '', "header"))],
      },
    ].concat(
      ...detailRows
    );

    return rows;
  };

  cell(text: string, styleId?: string): ExcelCell {
    return {
      styleId: styleId,
      data: {
        type: /^\d+$/.test(text) ? "Number" : "String",
        value: text,
      },
    };
  }

  exportSharesListInGroup(
    gridApi: GridApi,
    colDefs: ColDef[],
    rowData: any[],
    exportType: ExportTypeEnum,
  ): void {
    const modalRef = this.modalFormsService.openModal(ColumnForExportComponent, { modalDialogClass: 'export-company-list' });
    const instance = modalRef.componentInstance as ColumnForExportComponent;

    instance.title = 'Export Share List';
    instance.subTitle = 'shares selected';
    instance.colDefs = colDefs as ColGroupDef[];
    instance.numberOfCompanies = rowData.length;
    instance.exportType = exportType;
    instance.confirm
      .pipe(take(1))
      .subscribe((columnForExport: string[]) => {
        if (exportType === ExportTypeEnum.EXCEL) {
          gridApi.exportDataAsExcel({ columnKeys: columnForExport });
        } else if (exportType === ExportTypeEnum.CSV) {
          gridApi.exportDataAsCsv({ columnKeys: columnForExport });
        }
      });
  }
}
